/**
 * Question Banks — single-bank detail view (lifecycle, usage, item list).
 *
 * Extracted verbatim from QuestionBanksPage.jsx during the Phase 5 refactor
 * (no behaviour change). Data/mutations live in useBankDetail.
 */
import { useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { LumynBadge } from '../common/LumynBadge';
import { EmptyState } from '../common/EmptyState';
import { SkeletonCard } from '../common/SkeletonCard';
import {
  Trash2, ChevronLeft, Tag, FileText, History, Activity, Pencil,
  CheckCircle2, Plus, FileSpreadsheet,
} from 'lucide-react';
import { downloadAuthedFile } from '../../utils/downloadAuthedFile';
import BankItemEditorDialog from './BankItemEditorDialog';
import BankItemHistoryDialog from './BankItemHistoryDialog';
import ImportCsvDialog from './ImportCsvDialog';
import { BankBulkActionBar } from './BankBulkActionBar';
import { Checkbox } from '../ui/checkbox';
import { NEXT_STATUS, STATUS_PILL_CLS } from './banksMeta';
import { CalibrationPip } from './CalibrationPip';
import { BankActionsMenu } from './BankActionsMenu';
import { PublishBankDialog } from './PublishBankDialog';
import { useBankDetail, isUnpublished } from '../../hooks/useBankDetail';

export function BankDetail({ bank, onBack, onChanged, onEdit, onDuplicate, onRetire, onDelete, canEdit = true }) {
  const {
    items, loadingItems, usage, loadItems,
    handleTransitionBank, handlePublishAllItems, handleTransitionItem, handleRetireItem,
  } = useBankDetail(bank.bank_id, onChanged);
  const unpublishedItems = items.filter(isUnpublished);
  const unpublishedCount = unpublishedItems.length;
  const showRepair = canEdit && bank.lifecycle_status === 'published' && unpublishedCount > 0;
  const [publishMode, setPublishMode] = useState(null); // 'bank' | 'items' | null
  const onBankTransition = (target) => target === 'published' ? setPublishMode('bank') : handleTransitionBank(target);
  const openRepair = () => setPublishMode('items');
  const { isSuperAdmin, isOrgAdmin } = useAuth();
  const canRevive = isSuperAdmin() || isOrgAdmin();
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null); // null = create
  const [historyItem, setHistoryItem] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [statusFilter, setStatusFilter] = useState('all');

  const visibleItems = statusFilter === 'all' ? items : items.filter(it => it.status === statusFilter);
  const selectedItems = items.filter(it => selected.has(it.item_id));
  const allVisibleSelected = visibleItems.length > 0 && visibleItems.every(it => selected.has(it.item_id));

  const toggleSelect = (itemId) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(itemId)) next.delete(itemId); else next.add(itemId);
    return next;
  });
  const toggleSelectAll = () => setSelected(prev => {
    if (allVisibleSelected) {
      const next = new Set(prev);
      visibleItems.forEach(it => next.delete(it.item_id));
      return next;
    }
    return new Set([...prev, ...visibleItems.map(it => it.item_id)]);
  });
  const clearSelection = () => setSelected(new Set());
  const changeFilter = (f) => { setStatusFilter(f); setSelected(new Set()); };

  const openCreate = () => { setEditingItem(null); setEditorOpen(true); };
  const openEdit = (it) => { setEditingItem(it); setEditorOpen(true); };
  const openHistory = (it) => { setHistoryItem(it); };

  const pillCls = STATUS_PILL_CLS[bank.lifecycle_status] || STATUS_PILL_CLS.draft;

  return (
    <div className="space-y-4" data-testid="bank-detail">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onBack}><ChevronLeft className="w-4 h-4" />Back</Button>
        <h1 className="text-xl font-bold flex-1 truncate">{bank.title}</h1>
        <span
          className={`shrink-0 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${pillCls}`}
          data-testid={`bank-detail-status-${bank.bank_id}`}
        >
          {bank.lifecycle_status.replace('_', ' ')}
        </span>
        {canEdit && (
          <BankActionsMenu
            bank={bank}
            onEdit={onEdit}
            onDuplicate={onDuplicate}
            onRetire={onRetire}
            onDelete={onDelete}
            onPublishItems={showRepair ? openRepair : null}
            triggerSize="md"
          />
        )}
      </div>
      {bank.description && <p className="text-sm text-muted-foreground">{bank.description}</p>}

      {showRepair && (
        <div className="flex items-center justify-between gap-3 px-4 py-2.5 border rounded-md bg-card"
             data-testid="bank-unpublished-notice">
          <p className="text-sm">
            <span className="font-medium">{unpublishedCount} question{unpublishedCount === 1 ? '' : 's'}</span>
            <span className="text-muted-foreground"> in this published bank {unpublishedCount === 1 ? 'is' : 'are'} not yet published — Live Quiz and assessments cannot draw {unpublishedCount === 1 ? 'it' : 'them'}.</span>
          </p>
          <Button size="sm" onClick={openRepair} data-testid="bank-publish-all-items-btn">
            <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> Publish all items
          </Button>
        </div>
      )}

      {/* Bank lifecycle controls — editor+ only */}
      {canEdit && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-xs">Bank lifecycle</CardTitle></CardHeader>
          <CardContent className="flex flex-wrap gap-2 pt-0">
            {(NEXT_STATUS[bank.lifecycle_status] || []).map(target => (
              <Button key={target} variant="outline" size="sm" onClick={() => onBankTransition(target)} data-testid={`bank-trans-${target}`}>
                → {target}
              </Button>
            ))}
            {NEXT_STATUS[bank.lifecycle_status]?.length === 0 && <span className="text-xs text-muted-foreground">Terminal state — contact super-admin to revive.</span>}
          </CardContent>
        </Card>
      )}

      {/* Reverse-index usage — where is this bank actually referenced? */}
      {usage && usage.total > 0 && (
        <Card data-testid="bank-usage-block">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs flex items-center gap-1.5">
              <Activity className="w-3.5 h-3.5 text-[var(--lumyn-banks)]" />
              Used in {usage.total} place{usage.total === 1 ? '' : 's'}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 space-y-2.5 text-xs">
            {usage.quizzes.length > 0 && (
              <div>
                <div className="text-muted-foreground uppercase tracking-wider text-[10px] mb-1">
                  Quizzes ({usage.quizzes.length})
                </div>
                <ul className="space-y-1">
                  {usage.quizzes.map(q => (
                    <li key={q.quiz_id} className="flex items-center justify-between gap-2" data-testid={`usage-quiz-${q.quiz_id}`}>
                      <span className="truncate">
                        <span className="font-medium">{q.title}</span>
                        {q.module_title && <span className="text-muted-foreground"> · {q.module_title}</span>}
                      </span>
                      <span className="shrink-0 text-[10px] text-muted-foreground">
                        sample {q.sample_size ?? '?'}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {usage.live_sessions.length > 0 && (
              <div>
                <div className="text-muted-foreground uppercase tracking-wider text-[10px] mb-1">
                  Live sessions ({usage.live_sessions.length})
                </div>
                <ul className="space-y-1">
                  {usage.live_sessions.slice(0, 8).map(s => (
                    <li key={s.session_id} className="flex items-center justify-between gap-2" data-testid={`usage-live-${s.session_id}`}>
                      <span className="truncate">
                        <span className="font-mono text-[10px] text-muted-foreground">{s.ref_code}</span>
                        <span className="ml-1.5">{s.title || 'Live session'}</span>
                      </span>
                      <LumynBadge size="sm" variant={s.status === 'ended' ? 'expired' : s.status === 'lobby' ? 'in_progress' : 'completed'}>{s.status}</LumynBadge>
                    </li>
                  ))}
                  {usage.live_sessions.length > 8 && (
                    <li className="text-muted-foreground italic">…and {usage.live_sessions.length - 8} more.</li>
                  )}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Items */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-display font-semibold flex items-center gap-2"><FileText className="w-4 h-4 text-[var(--lumyn-banks)]" />Items ({items.length})</h2>
        <div className="flex items-center gap-2">
          {canEdit && (
            <Button
              size="sm"
              variant="outline"
              onClick={async () => {
                // Authenticated Blob download — see utils/downloadAuthedFile
                // for the why. `target=_blank` strips the Authorization header
                // on some preview deploys and the user sees "404 page not found"
                // (actually an upstream-401 wrapped by the edge proxy).
                try {
                  const safe = (bank.title || 'bank').replace(/[^a-zA-Z0-9-_ ]/g, '_').slice(0, 60);
                  await downloadAuthedFile(
                    `/question-banks/${bank.bank_id}/export.csv`,
                    `${safe}-items.csv`,
                  );
                } catch (e) {
                  const status = e?.response?.status;
                  toast.error(
                    status === 401
                      ? 'Your session has expired — please sign in again.'
                      : status === 403
                        ? 'Only editors can export this bank.'
                        : status === 404
                          ? 'Bank not found.'
                          : 'Export failed. Try again in a moment.',
                  );
                }
              }}
              data-testid="export-csv-btn"
              className="border-[var(--lumyn-banks)]/40 text-[var(--lumyn-banks)] hover:bg-[var(--lumyn-banks)]/10"
              title="Download all current items as a CSV (re-importable)"
            >
              <FileSpreadsheet className="w-4 h-4 mr-1" />Export CSV
            </Button>
          )}
          {canEdit && (
            <>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setImportOpen(true)}
                data-testid="import-csv-btn"
                className="border-[var(--lumyn-banks)]/40 text-[var(--lumyn-banks)] hover:bg-[var(--lumyn-banks)]/10"
              >
                <FileSpreadsheet className="w-4 h-4 mr-1" />Import CSV
              </Button>
              <Button size="sm" onClick={openCreate} data-testid="add-item-btn" className="bg-[var(--lumyn-banks)] hover:bg-[var(--lumyn-banks)]/90 text-white"><Plus className="w-4 h-4 mr-1" />Add item</Button>
            </>
          )}
        </div>
      </div>
      {loadingItems ? <SkeletonCard /> : items.length === 0 ? (
        <EmptyState icon={FileText} title="No items yet" description="Add your first question to start building this bank." />
      ) : (
        <>
          {/* Status filter + select-all header */}
          <div className="flex flex-wrap items-center gap-2">
            {canEdit && (
              <label className="flex items-center gap-2 text-xs font-medium cursor-pointer select-none pr-1">
                <Checkbox
                  checked={allVisibleSelected}
                  onCheckedChange={toggleSelectAll}
                  data-testid="select-all-items"
                />
                Select all{statusFilter !== 'all' ? ` (${statusFilter.replace('_', ' ')})` : ''}
              </label>
            )}
            <div className="flex flex-wrap gap-1 ml-auto" data-testid="item-status-filter">
              {['all', 'draft', 'in_review', 'approved', 'published', 'retired'].map(f => {
                const n = f === 'all' ? items.length : items.filter(it => it.status === f).length;
                return (
                  <button
                    key={f}
                    type="button"
                    onClick={() => changeFilter(f)}
                    data-testid={`item-filter-${f}`}
                    className={`px-2 py-1 rounded-md border text-[11px] transition-colors ${
                      statusFilter === f
                        ? 'border-[var(--lumyn-banks)] bg-[var(--lumyn-banks)]/10 text-[var(--lumyn-banks)] font-medium'
                        : 'text-muted-foreground hover:border-foreground/30'
                    }`}
                  >
                    {f.replace('_', ' ')} ({n})
                  </button>
                );
              })}
            </div>
          </div>

          {visibleItems.length === 0 ? (
            <EmptyState icon={FileText} title="No items in this state" description="Change the filter above to see other questions." />
          ) : (
          <div className="space-y-2">
          {visibleItems.map(it => (
            <div key={it.item_id} className={`border rounded-lg p-3 space-y-2 bg-card transition ${selected.has(it.item_id) ? 'border-[var(--lumyn-banks)] ring-1 ring-[var(--lumyn-banks)]/30' : 'hover:border-[var(--lumyn-banks)]/40'}`} data-testid={`bank-item-${it.item_id}`}>
              <div className="flex items-start gap-2">
                {canEdit && (
                  <Checkbox
                    className="mt-0.5"
                    checked={selected.has(it.item_id)}
                    onCheckedChange={() => toggleSelect(it.item_id)}
                    data-testid={`select-item-${it.item_id}`}
                  />
                )}
                <span className="flex-1 text-sm font-medium">{it.question_text}</span>
                <LumynBadge size="sm" variant={it.status} />
              </div>

              {/* Options preview — green-highlighted correct */}
              {it.options?.length > 0 && (
                <div className="space-y-1 pl-1">
                  {it.options.map((o, i) => (
                    <div
                      key={`${it.item_id}-opt-${i}`}
                      className={`flex items-center gap-2 text-xs px-2 py-1 rounded ${o.is_correct ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 font-medium' : 'text-muted-foreground'}`}
                    >
                      {o.is_correct
                        ? <CheckCircle2 className="w-3 h-3 shrink-0" />
                        : <span className="w-3 h-3 rounded-full border border-muted-foreground/30 shrink-0" />}
                      <span className="truncate">{o.text}</span>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex flex-wrap gap-2 text-[11px] text-muted-foreground items-center pt-1 border-t">
                <span>{it.question_type}</span>
                {it.difficulty && <span>· {it.difficulty}</span>}
                {it.bloom_level && <span>· {it.bloom_level}</span>}
                {it.language && <span>· {it.language}</span>}
                <span className="inline-flex items-center gap-0.5"><History className="w-3 h-3" />v{it.version}</span>
                {it.tags?.length > 0 && <span className="inline-flex items-center gap-0.5"><Tag className="w-3 h-3" />{it.tags.slice(0, 3).join(', ')}</span>}
                <CalibrationPip calibration={it.calibration} />
              </div>

              {canEdit && (
                <div className="flex flex-wrap gap-1.5">
                  {it.status !== 'retired' && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 text-[11px]"
                      onClick={() => openEdit(it)}
                      data-testid={`edit-item-${it.item_id}`}
                    >
                      <Pencil className="w-3 h-3 mr-1" />Edit
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-[11px]"
                    onClick={() => openHistory(it)}
                    data-testid={`history-item-${it.item_id}`}
                  >
                    <History className="w-3 h-3 mr-1" />History
                  </Button>
                  {(NEXT_STATUS[it.status] || []).map(target => (
                    <Button key={target} variant="outline" size="sm" className="h-7 text-[11px]" onClick={() => handleTransitionItem(it.item_id, target)}>
                      → {target}
                    </Button>
                  ))}
                  {it.status === 'retired' && (
                    canRevive ? (
                      <Button variant="outline" size="sm" className="h-7 text-[11px]"
                              onClick={() => handleTransitionItem(it.item_id, 'draft')}
                              data-testid={`revive-item-${it.item_id}`}>
                        Revive → draft
                      </Button>
                    ) : (
                      <span className="text-[11px] text-muted-foreground italic">Retired — read-only. Ask an admin to revive.</span>
                    )
                  )}
                  {it.status !== 'retired' && (
                    <Button variant="ghost" size="sm" className="h-7 text-[11px] text-red-500" onClick={() => handleRetireItem(it.item_id)}>
                      <Trash2 className="w-3 h-3 mr-1" />Retire
                    </Button>
                  )}
                </div>
              )}
            </div>
          ))}
          </div>
          )}

          {canEdit && (
            <BankBulkActionBar
              bankId={bank.bank_id}
              selectedItems={selectedItems}
              onClear={clearSelection}
              onDone={() => { clearSelection(); loadItems(); onChanged?.(); }}
            />
          )}
        </>
      )}

      <PublishBankDialog
        open={!!publishMode}
        onOpenChange={(o) => !o && setPublishMode(null)}
        items={unpublishedItems}
        mode={publishMode || 'bank'}
        onConfirm={(exclude) => publishMode === 'bank' ? handleTransitionBank('published', exclude) : handlePublishAllItems(exclude)}
      />
      <BankItemEditorDialog
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        bankId={bank.bank_id}
        item={editingItem}
        onSaved={() => { loadItems(); onChanged?.(); }}
      />
      <BankItemHistoryDialog
        open={!!historyItem}
        onClose={() => setHistoryItem(null)}
        bankId={bank.bank_id}
        item={historyItem}
      />
      <ImportCsvDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        bankId={bank.bank_id}
        onImported={() => { loadItems(); onChanged?.(); }}
      />
    </div>
  );
}
